<script>
  import { onDestroy, onMount, tick } from 'svelte';
  import { ArrowDown, LoaderCircle, MessageCircle } from 'lucide-svelte';
  import {
    hasMore,
    loadMessageContextWS,
    loadMessagesWS,
    loadPinnedMessagesWS,
    messages,
    pinnedMessages,
    unpinMessageWS
  } from '$lib/websocket-client.js';
  import { isDifferentDay } from '$lib/utils/datetime';
  import TimelineDivider from '@components/messenger/TimelineDivider.svelte';
  import MessageBubble from './MessageBubble.svelte';
  import PinnedMessagesBar from './PinnedMessagesBar.svelte';

  export let items = [];
  export let conversationId = null;
  export let myId = null;
  export let selected = false;
  export let typing = null;
  export let onShareMessage = null;
  export let interactionMode = 'desktop';
  export let emptyText = 'Chọn một cuộc trò chuyện!';

  let viewport;
  let loadingOlder = false;
  let reachedStart = false;
  let atBottom = true;
  let showJump = false;
  let newMessageCount = 0;
  let lastLoadedFirstId = null;
  let lastMessageId = null;
  let trackedConversation = null;
  let scrollFrame = null;

  $: activePinnedState =
    String($pinnedMessages.conversationId ?? '') === String(conversationId ?? '')
      ? $pinnedMessages
      : { conversationId, rows: [], loading: false, error: '' };
  $: pinnedIds = new Set(activePinnedState.rows.map((row) => String(row.id)));

  $: if (
    selected &&
    conversationId &&
    String($pinnedMessages.conversationId ?? '') !== String(conversationId)
  ) {
    loadPinnedMessagesWS(conversationId);
  }

  $: {
    const nextConversation = String(conversationId ?? '');
    if (nextConversation !== trackedConversation) {
      trackedConversation = nextConversation;
      reachedStart = false;
      atBottom = true;
      showJump = false;
      newMessageCount = 0;
      lastLoadedFirstId = null;
      lastMessageId = null;
    }

    const nextLastId = items[items.length - 1]?.id ?? null;
    if (nextLastId && nextLastId !== lastMessageId) {
      const isFirstBatch = !lastMessageId;
      lastMessageId = nextLastId;
      handleLastMessageChange(isFirstBatch);
    }
  }

  async function handleLastMessageChange(isFirstBatch) {
    await tick();
    if (!viewport) return;

    if (isFirstBatch || atBottom) {
      await scrollToBottom(false);
      newMessageCount = 0;
      showJump = false;
      return;
    }

    newMessageCount = Math.min(newMessageCount + 1, 99);
    showJump = true;
  }

  function updateScrollState() {
    if (!viewport) return;

    const distance = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    atBottom = distance < 140;
    showJump = !atBottom;

    if (atBottom) newMessageCount = 0;
  }

  function handleScroll() {
    if (scrollFrame) cancelAnimationFrame(scrollFrame);

    scrollFrame = requestAnimationFrame(() => {
      updateScrollState();

      if (viewport?.scrollTop <= 120) {
        loadOlder();
      }
    });
  }

  async function loadOlder() {
    if (!viewport || loadingOlder || reachedStart || !$hasMore || !conversationId || !items.length) return [];

    const firstMessage = items[0];
    if (!firstMessage?.id || firstMessage.id === lastLoadedFirstId) return [];

    loadingOlder = true;
    lastLoadedFirstId = firstMessage.id;

    const previousHeight = viewport.scrollHeight;
    const previousTop = viewport.scrollTop;

    try {
      const rows = await loadMessagesWS(conversationId, { firstMsg: firstMessage });
      await tick();

      if (!rows.length) {
        reachedStart = !$hasMore;
        if (!reachedStart) lastLoadedFirstId = null;
        return rows;
      }

      const heightDelta = viewport.scrollHeight - previousHeight;
      viewport.scrollTop = previousTop + Math.max(0, heightDelta);
      return rows;
    } catch (error) {
      lastLoadedFirstId = null;
      console.error('Load older messages failed:', error);
      return [];
    } finally {
      loadingOlder = false;
      updateScrollState();
    }
  }

  export async function scrollToBottom(smooth = false) {
    if (!viewport) return;

    await tick();
    viewport.scrollTo({
      top: viewport.scrollHeight,
      behavior: smooth ? 'smooth' : 'auto'
    });
    atBottom = true;
    showJump = false;
    newMessageCount = 0;
  }

  async function jumpToMessage(messageId, targetConversationId = conversationId) {
    if (!messageId) return;
    if (String(targetConversationId ?? '') !== String(conversationId ?? '')) return;

    let element = document.getElementById(`msg-${messageId}`);

    if (!element) {
      const rows = await loadMessageContextWS(conversationId, messageId);
      if (rows.length) {
        messages.set(rows);
        reachedStart = false;
      }
      await tick();
      element = document.getElementById(`msg-${messageId}`);
    }

    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    element.classList.remove('flash-highlight');
    void element.offsetWidth;
    element.classList.add('flash-highlight');
    setTimeout(() => element?.classList.remove('flash-highlight'), 1800);
  }

  function handleSearchJump(event) {
    jumpToMessage(event.detail?.messageId, event.detail?.conversationId);
  }

  onMount(() => {
    window.addEventListener('search-jump-to-message', handleSearchJump);
  });

  onDestroy(() => {
    window.removeEventListener('search-jump-to-message', handleSearchJump);
    if (scrollFrame) cancelAnimationFrame(scrollFrame);
  });
</script>

<section class="message-viewport">
  {#if selected}
    <PinnedMessagesBar
      rows={activePinnedState.rows}
      {myId}
      loading={activePinnedState.loading}
      error={activePinnedState.error}
      onJump={(row) => jumpToMessage(row.id, row.conversationId)}
      onUnpin={(row) => unpinMessageWS(conversationId, row.id)} />
  {/if}

  <div
    bind:this={viewport}
    on:scroll={handleScroll}
    on:contextmenu|preventDefault
    class="message-scroll">
    {#if !selected}
      <div class="empty-state">
        <div class="empty-icon"><MessageCircle size={28} strokeWidth={1.8} /></div>
        <p>{emptyText}</p>
      </div>
    {:else}
      {#if loadingOlder}
        <div class="load-state"><LoaderCircle size={15} class="animate-spin" /> Đang tải tin nhắn cũ</div>
      {:else if reachedStart && items.length}
        <div class="load-state">Đã đến đầu cuộc trò chuyện</div>
      {/if}

      {#each items as msg, index (msg.id)}
        {#if index === 0 || isDifferentDay(msg.created_at, items[index - 1]?.created_at)}
          <TimelineDivider time={msg.created_at} locale="vi" />
        {/if}
        <MessageBubble
          {msg}
          mediaMessages={items}
          {myId}
          previousMsg={items[index - 1]}
          {onShareMessage}
          {interactionMode}
          isPinned={pinnedIds.has(String(msg.id))} />
      {/each}

      <div class="h-2"></div>
    {/if}
  </div>

  {#if typing && typing.size > 0}
    <div class="typing-pill">
      <span></span>
      <span></span>
      <span></span>
      <strong>{typing.size === 1 ? 'Đang nhập...' : 'Nhiều người đang nhập...'}</strong>
    </div>
  {/if}

  {#if showJump}
    <button class="jump-button" aria-label="Cuộn xuống tin nhắn mới" on:click={() => scrollToBottom(true)}>
      <ArrowDown size={20} />
      {#if newMessageCount > 0}
        <span>{newMessageCount > 99 ? '99+' : newMessageCount}</span>
      {/if}
    </button>
  {/if}
</section>

<style>
  .message-viewport {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    overflow: hidden;
    background: var(
      --messenger-content-background,
      radial-gradient(circle at 18% 0%, rgba(59, 130, 246, 0.08), transparent 26rem),
      radial-gradient(circle at 82% 100%, rgba(148, 163, 184, 0.08), transparent 24rem),
      linear-gradient(180deg, #f8fafc 0%, #f4f7fb 54%, #eef2f7 100%)
    );
    background-size: var(--messenger-content-background-size, cover);
    background-position: var(--messenger-content-background-position, center);
    background-repeat: var(--messenger-content-background-repeat, no-repeat);
  }

  .message-scroll {
    min-height: 0;
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    scroll-behavior: auto;
    padding: 1rem 0.95rem 1.1rem;
    scrollbar-gutter: stable;
  }

  .empty-state {
    display: grid;
    place-items: center;
    align-content: center;
    gap: 0.8rem;
    min-height: 100%;
    color: #64748b;
    text-align: center;
  }

  .empty-icon {
    display: grid;
    place-items: center;
    width: 4rem;
    height: 4rem;
    border: 1px solid rgba(148, 163, 184, 0.3);
    border-radius: 1.3rem;
    color: #2563eb;
    background: rgba(255, 255, 255, 0.72);
    box-shadow: 0 16px 40px rgba(30, 64, 175, 0.12);
    backdrop-filter: blur(18px);
  }

  .load-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    padding: 0.45rem 0.3rem;
    color: #64748b;
    font-size: 0.74rem;
  }

  .typing-pill {
    position: absolute;
    bottom: 0.65rem;
    left: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.4rem 0.65rem;
    border: 1px solid rgba(148, 163, 184, 0.28);
    border-radius: 999px;
    color: #64748b;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1);
    backdrop-filter: blur(18px);
    pointer-events: none;
  }

  .typing-pill span {
    width: 0.23rem;
    height: 0.23rem;
    border-radius: 999px;
    background: #64748b;
    animation: pulse 1s infinite alternate;
  }

  .typing-pill span:nth-child(2) {
    animation-delay: 0.15s;
  }

  .typing-pill span:nth-child(3) {
    animation-delay: 0.3s;
  }

  .typing-pill strong {
    margin-left: 0.25rem;
    font-size: 0.68rem;
    font-weight: 500;
  }

  .jump-button {
    position: absolute;
    right: 0.75rem;
    bottom: 0.75rem;
    z-index: 20;
    display: grid;
    place-items: center;
    width: 2.75rem;
    height: 2.75rem;
    border: 1px solid rgba(148, 163, 184, 0.32);
    border-radius: 999px;
    color: #1e40af;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 12px 26px rgba(30, 64, 175, 0.14);
    backdrop-filter: blur(18px);
  }

  .jump-button span {
    position: absolute;
    top: -0.3rem;
    right: -0.25rem;
    min-width: 1.1rem;
    height: 1.1rem;
    padding: 0 0.25rem;
    border-radius: 999px;
    color: white;
    background: #ef4444;
    font-size: 0.6rem;
    line-height: 1.1rem;
  }

  @keyframes pulse {
    to {
      transform: translateY(-0.15rem);
      opacity: 0.45;
    }
  }

  @media (max-width: 640px) {
    .message-scroll {
      padding: 0.75rem 0.65rem 0.85rem 0.55rem;
    }

    .typing-pill {
      left: 0.55rem;
      /* right: 4rem; */
    }

    .jump-button {
      right: 0.55rem;
      bottom: 5rem;
      z-index: 20;
    }
  }
</style>
